import { useNamespacesPulsarAdmin } from '@/api/pulsarAdmin'
import type {
  RetentionPolicies,
  DispatchRateImpl,
  SubscribeRate,
  BacklogQuota
} from '@streamnative/pulsar-admin-client-typescript'
import { isEqual } from 'lodash-es'

export type PulsarAuthorization = { role: string; permissions: string[] }

// TODO, this function hides error prone functionality with the default values. we should refactor this
// to be explicit.
const buildNamespacePulsarAdmin = (
  organization: string | undefined = usePulsarState().organization.value,
  clusterUid: string | undefined = usePulsarState().clusterUid.value,
  instance?: string
) => {
  if (!organization) {
    throw Error(`Organization is not set.`)
  }
  if (!clusterUid) {
    throw Error(`Cluster is not set.`)
  }
  return useNamespacesPulsarAdmin(organization, clusterUid, instance)
}

export const fetchNamespaces = async ({
  organization,
  clusterUid,
  tenant,
  instance
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  instance?: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid, instance)
  const res = await namespacePulsarAdmin.getTenant(tenant, { cache: true })
  // this is funky??
  res.data = res.data.map(namespace => {
    return namespace.replace(`${tenant}/`, '')
  })
  return res
}

export const fetchNamespacePolicies = ({
  organization,
  clusterUid,
  tenant,
  namespace
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.getPolicies(tenant, namespace)
}

export const createNamespace = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  options
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  options?: { [key: string]: string | string[] }
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.createNamespace(tenant, namespace, options)
}

export const deleteNamespace = ({
  organization,
  clusterUid,
  tenant,
  namespace
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.deleteNamespace(tenant, namespace)
}

export const getPermissions = ({
  organization,
  clusterUid,
  tenant,
  namespace
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.getPermissions(tenant, namespace)
}

export const grantPermissions = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  role,
  permissions
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  role: string
  permissions: string[]
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.grantPermissionOnNamespace(tenant, namespace, role, permissions)
}

export const revokePermissions = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  role
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  role: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.revokePermissionsOnNamespace(tenant, namespace, role)
}

export const setBacklogQuota = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  backlogQuotaType,
  backlogQuota
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  backlogQuotaType: 'destination_storage'
  backlogQuota: BacklogQuota
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setBacklogQuota(tenant, namespace, backlogQuotaType, backlogQuota)
}

export const setMessageTtl = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  messageTtl
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  messageTtl: number
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setNamespaceMessageTTL(tenant, namespace, messageTtl)
}

export const getSubscriptionExpirationTime = ({
  organization,
  clusterUid,
  tenant,
  namespace
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.getSubscriptionExpirationTime(tenant, namespace)
}

export const setSubscriptionExpirationTime = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  expirationTimeInMinutes
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  expirationTimeInMinutes: number | null | undefined
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  if (expirationTimeInMinutes === undefined || expirationTimeInMinutes === null) {
    return namespacePulsarAdmin.removeSubscriptionExpirationTime(tenant, namespace)
  }
  return namespacePulsarAdmin.setSubscriptionExpirationTime(
    tenant,
    namespace,
    expirationTimeInMinutes
  )
}

// todo: why have this? this information is fetched in getPolicies res.retention_policies
export const getRetention = ({
  organization,
  clusterUid,
  tenant,
  namespace
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.getRetention(tenant, namespace)
}

export const updateRetention = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  retentionPolicies
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  retentionPolicies: RetentionPolicies
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  // When a user unsets input values in the UI, the policy should be removed.
  // When set to blank, these values are actually strings.
  if (
    String(retentionPolicies.retentionTimeInMinutes) === '' &&
    String(retentionPolicies.retentionSizeInMB) === ''
  ) {
    return namespacePulsarAdmin.removeRetention(tenant, namespace, retentionPolicies)
  } else {
    return namespacePulsarAdmin.setRetention(tenant, namespace, retentionPolicies)
  }
}

export const unloadOnCluster = ({
  organization,
  clusterUid,
  tenant,
  namespace
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.unloadNamespace(tenant, namespace)
}

export const unloadBundleOnCluster = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  bundle
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  bundle: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.unloadNamespaceBundle(tenant, namespace, bundle)
}

export const setDispatchRate = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  topicDispatchRate
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  topicDispatchRate: DispatchRateImpl
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setDispatchRate(tenant, namespace, topicDispatchRate)
}

export const setSubscribeRate = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  clusterSubscribeRate
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  clusterSubscribeRate: SubscribeRate
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setSubscribeRate(tenant, namespace, clusterSubscribeRate)
}

export const setSubscriptionDispatchRate = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  subscriptionDispatchRate
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  subscriptionDispatchRate: DispatchRateImpl
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setSubscriptionDispatchRate(
    tenant,
    namespace,
    subscriptionDispatchRate
  )
}

export const clearBacklogOnCluster = ({
  organization,
  clusterUid,
  tenant,
  namespace
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.clearNamespaceBacklog(tenant, namespace)
}
export const clearBundleBacklogOnCluster = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  bundle
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  bundle: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.clearNamespaceBundleBacklog(tenant, namespace, bundle)
}

export const setNamespaceReplicationClusters = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  clusters
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  clusters: string[]
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setNamespaceReplicationClusters(tenant, namespace, clusters)
}

export const setSubscriptionAuthMode = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  mode
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  mode: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setSubscriptionAuthMode(tenant, namespace, mode)
}

export const setCompactionThreshold = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  compactionThreshold
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  compactionThreshold: number
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setCompactionThreshold(tenant, namespace, compactionThreshold)
}

export const setSchemaAutoUpdateStrategy = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  strategy
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  strategy: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setSchemaAutoUpdateCompatibilityStrategy(tenant, namespace, strategy)
}

export const setSchemaValidationEnforced = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  enforced
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  enforced: boolean
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setSchemaValidtionEnforced(tenant, namespace, enforced)
}

export const setMaxProducersPerTopic = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  maxCount
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  maxCount: number
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setMaxProducersPerTopic(tenant, namespace, maxCount)
}

export const setMaxConsumersPerTopic = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  maxCount
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  maxCount: number
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setMaxConsumersPerTopic(tenant, namespace, maxCount)
}

export const setMaxConsumersPerSubscription = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  maxCount
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  maxCount: number
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setMaxConsumersPerSubscription(tenant, namespace, maxCount)
}

export const updateAuthorization = async (
  tenant: string,
  namespace: string,
  newValues: PulsarAuthorization[],
  oldValues: PulsarAuthorization[]
) => {
  // const newValues = values.authorizations || []
  // const oldValues = authorizations.value || []

  for (const _auth of newValues) {
    // loop through the new values, and update permissions as necessary
    const newPermissions = _auth.permissions.sort()
    const oldPermissions = oldValues.find(a => a.role === _auth.role)

    if (newPermissions.length === 0) {
      await revokePermissions({
        tenant: tenant,
        namespace: namespace,
        role: _auth.role
      })
    } else if (!isEqual(newPermissions, oldPermissions?.permissions)) {
      await grantPermissions({
        tenant: tenant,
        namespace: namespace,
        role: _auth.role,
        permissions: newPermissions
      })
    }
  }

  for (const _auth of oldValues) {
    // loop through the old values, and revoke permissions if doesn't exists in the new values
    const newPermissions = newValues.find(
      (a: { role: string; permissions: string[] }) => a.role === _auth.role
    )

    if (!newPermissions) {
      await revokePermissions({
        tenant: tenant,
        namespace: namespace,
        role: _auth.role
      })
    }
  }
}

export const setProperty = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  propertyName,
  propertyValue
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  propertyName: string
  propertyValue: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setProperty(tenant, namespace, propertyName, propertyValue)
}

export const removeProperty = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  propertyName
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  propertyName: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.removeProperty(tenant, namespace, propertyName)
}

export const getProperty = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  propertyName
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  propertyName: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.getProperty(tenant, namespace, propertyName)
}

export const setProperties = ({
  organization,
  clusterUid,
  tenant,
  namespace,
  properties
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
  properties: { [key: string]: string }
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.setProperties(tenant, namespace, properties)
}

export const getProperties = ({
  organization,
  clusterUid,
  tenant,
  namespace
}: {
  organization?: string
  clusterUid?: string
  tenant: string
  namespace: string
}) => {
  const namespacePulsarAdmin = buildNamespacePulsarAdmin(organization, clusterUid)
  return namespacePulsarAdmin.getProperties(tenant, namespace)
}
